import { useState } from "react";
import { Box, IconButton, Paper, Typography } from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";

export default function LookupCard({
  label,
  items,
  bandColor,
  canCreate = true,
  canEdit = true,
  canDelete = true,
  onAdd,
  onEdit,
  onDelete,
  onReorder,
}) {
  const [draggedId, setDraggedId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const handleDragStart = (id) => (event) => {
    setDraggedId(id);
    event.dataTransfer.setData("text/plain", String(id));
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDropTarget(null);
  };

  const handleDragOver = (id) => (event) => {
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const position =
      event.clientY < rect.top + rect.height / 2 ? "above" : "below";
    setDropTarget((prev) =>
      prev?.id === id && prev.position === position ? prev : { id, position },
    );
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const target = dropTarget;
    setDropTarget(null);
    if (draggedId === null || !target) return;

    const remaining = items
      .map((item) => item.id)
      .filter((id) => id !== draggedId);
    const anchorIndex = remaining.indexOf(target.id);
    if (anchorIndex === -1) return;

    const insertAt =
      target.position === "below" ? anchorIndex + 1 : anchorIndex;
    const reordered = [...remaining];
    reordered.splice(insertAt, 0, draggedId);
    onReorder(reordered);
  };

  const styles = {
    card: {
      bgcolor: "background.cardBackground",
      border: 1,
      borderColor: "background.tableHeader",
      borderRadius: 2,
      overflow: "hidden",
    },
    cardBody: { p: 2 },
    cardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: 1.5,
    },
    cardLabel: {
      fontSize: "0.75rem",
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      fontWeight: 700,
      color: "background.textMuted",
    },
    headerRight: { display: "flex", alignItems: "center", gap: 0.5 },
    cardCount: {
      fontSize: "0.75rem",
      fontWeight: 700,
      color: "background.textMuted",
    },
    itemRow: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      py: 0.25,
      cursor: canEdit ? "grab" : "default",
      "& .row-actions": { display: "none" },
      "&:hover .row-actions": { display: "flex" },
    },
    dropIndicatorAbove: {
      position: "absolute",
      top: -1,
      left: 0,
      right: 0,
      height: 2,
      bgcolor: "primary.main",
      borderRadius: 1,
    },
    dropIndicatorBelow: {
      position: "absolute",
      bottom: -1,
      left: 0,
      right: 0,
      height: 2,
      bgcolor: "primary.main",
      borderRadius: 1,
    },
    itemName: {
      fontSize: "0.875rem",
      flexGrow: 1,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    rowActions: { display: "flex", gap: 0 },
    band: { height: 4, bgcolor: bandColor },
    dot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
    btn: { p: 0.25 },
    btnIcon: { fontSize: 14 },
  };

  const itemRowSx =
    draggedId !== null
      ? { ...styles.itemRow, "&:hover .row-actions": { display: "none" } }
      : styles.itemRow;

  return (
    <Paper sx={styles.card}>
      <Box sx={styles.band} />
      <Box sx={styles.cardBody}>
        <Box sx={styles.cardHeader}>
          <Typography sx={styles.cardLabel}>{label}</Typography>
          <Box sx={styles.headerRight}>
            <Typography sx={styles.cardCount}>{items.length}</Typography>
            {canCreate && (
              <IconButton size="small" onClick={onAdd} sx={styles.btn}>
                <AddIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
        {items.map((item) => {
          const dotSx = {
            ...styles.dot,
            bgcolor: item.color || "primary.main",
          };
          return (
            <Box
              key={item.id}
              draggable={canEdit}
              onDragStart={canEdit ? handleDragStart(item.id) : undefined}
              onDragEnd={canEdit ? handleDragEnd : undefined}
              onDragOver={canEdit ? handleDragOver(item.id) : undefined}
              onDrop={canEdit ? handleDrop : undefined}
              sx={itemRowSx}
            >
              {dropTarget?.id === item.id &&
                dropTarget.position === "above" && (
                  <Box sx={styles.dropIndicatorAbove} />
                )}
              <Box sx={dotSx} />
              <Typography sx={styles.itemName}>{item.name}</Typography>
              {(canEdit || canDelete) && (
                <Box className="row-actions" sx={styles.rowActions}>
                  {canEdit && (
                    <IconButton
                      size="small"
                      onClick={() => onEdit(item)}
                      sx={styles.btn}
                    >
                      <EditIcon sx={styles.btnIcon} />
                    </IconButton>
                  )}
                  {canDelete && (
                    <IconButton
                      size="small"
                      onClick={() => onDelete(item)}
                      sx={styles.btn}
                    >
                      <DeleteIcon sx={styles.btnIcon} />
                    </IconButton>
                  )}
                </Box>
              )}
              {dropTarget?.id === item.id &&
                dropTarget.position === "below" && (
                  <Box sx={styles.dropIndicatorBelow} />
                )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}
